import { useState } from 'react';
import { Ban, Plus, Trash2 } from 'lucide-react';
import { EMPTY_EXCLUDED_GEO, type TargetingConfig } from '../../../types/metaAdDraft';
import {
  CUSTOM_LOCATION_RADIUS_BOUNDS,
  clampCustomLocationRadius,
  defaultCustomLocationRadius,
} from '../../../lib/metaAdTargetingShape';
import type { GeoMapPoint } from '../../../lib/metaAdGeoPoints';
import { clampRadiusForPoint, geoBoundaries, geoMapPoints, resolvedGeoPoints } from '../../../lib/metaAdGeoPoints';
import { EXCLUDED_CUSTOM_LOCATIONS_SUPPORTED } from '../../../lib/metaExclusionSupport';
import { searchInterests } from '../api';
import { type GeoItem, useResolvedGeoCenters } from '../geoCenterResolver';
import { useAuPostcodePolygons } from '../auPostcodes';
import { countryName, currencyToCountry, deriveDefaultCountry } from '../countries';
import { addGeoExclusion, applyGeoSelection } from '../geoHierarchy';
import type { UnifiedGeoResult } from '../geoSearch';
import { useLinkedAdAccount } from '../useLinkedAdAccount';
import { AudienceMap } from '../components/AudienceMap';
import { RadioGroup } from '../components/RadioGroup';
import { TargetingSearchField } from '../components/TargetingSearchField';
import { UnifiedGeoSearch } from '../components/UnifiedGeoSearch';
import { SelectedGeoChips } from '../components/SelectedGeoChips';
import { TargetingExtractionModal } from '../components/TargetingExtractionModal';
import { ExtractedTargetingSummary } from '../components/ExtractedTargetingSummary';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';

type AudienceMode = 'manual' | 'extracted';

const EMPTY_MANUAL_TARGETING: TargetingConfig = {
  geoLevel: 'country',
  countries: [],
  cities: [],
  regions: [],
  places: [],
  neighborhoods: [],
  zips: [],
  customLocations: [],
  excluded: EMPTY_EXCLUDED_GEO,
  locationTypes: 'home_recent',
  ageMin: 18,
  ageMax: 65,
  gender: 'all',
  interests: [],
  sourceType: 'manual',
  sourcePresetId: null,
  sourceCampaignId: null,
  sourceCampaignName: null,
  sourceAdSetId: null,
  sourceAdSetName: null,
  rawPayload: null,
};

// Has the AM entered any manual targeting worth confirming before we clear it?
function hasManualData(t: TargetingConfig): boolean {
  const e = t.excluded;
  const hasExcluded =
    e.regions.length > 0 ||
    e.cities.length > 0 ||
    e.places.length > 0 ||
    e.neighborhoods.length > 0 ||
    e.zips.length > 0 ||
    e.customLocations.length > 0;
  return (
    t.countries.length > 0 ||
    t.cities.length > 0 ||
    t.regions.length > 0 ||
    t.places.length > 0 ||
    t.neighborhoods.length > 0 ||
    t.zips.length > 0 ||
    t.customLocations.length > 0 ||
    hasExcluded ||
    t.interests.length > 0 ||
    t.gender !== 'all' ||
    t.ageMin !== 18 ||
    t.ageMax !== 65
  );
}

// Multi-ad-set: operates on ONE ad set's targeting (each ad set instantiates its
// own AudienceSection inside its card). `onChange` replaces this ad set's whole
// TargetingConfig; the caller wires it into adSets[i].targeting.
export function AudienceSection({
  targeting,
  onChange,
  accountId,
  initialMode,
}: {
  targeting: TargetingConfig;
  onChange: (next: TargetingConfig) => void;
  accountId: string;
  // Optional starting mode. The ad builder omits it (derives from the saved
  // targeting); the Meta Setup hub passes 'extracted' so its "Extract from
  // campaign" action opens straight into the extraction panel.
  initialMode?: AudienceMode;
}) {
  const t = targeting;
  const { currency, timezoneName } = useLinkedAdAccount(accountId);
  const country = t.countries[0] ?? null;
  const isExtracted = t.sourceType === 'extracted';

  // The account's likely country (ISO alpha-2). The geo search is GLOBAL (no hard
  // filter — so NZ + every country's results appear); this only BIASES the picker's
  // ranking toward the account's country and is stored as the picked geo's countryCode
  // fallback (publish/reach context; not sent to Meta). CURRENCY is the reliable signal
  // (NZD→NZ) — the old timezone proxy mis-scoped NZ clients billed to AU and hid their
  // cities; timezone + any selected country are weaker fallbacks.
  const biasCountry = currencyToCountry(currency) ?? deriveDefaultCountry(timezoneName) ?? country;

  const [mode, setMode] = useState<AudienceMode>(initialMode ?? (isExtracted ? 'extracted' : 'manual'));
  const [extractOpen, setExtractOpen] = useState(false);
  const [pendingMode, setPendingMode] = useState<AudienceMode | null>(null);
  // Search Include/Exclude toggle — Exclude routes a picked area into the excluded
  // bucket (and a dropped map pin becomes an excluded pin).
  const [geoMode, setGeoMode] = useState<'include' | 'exclude'>('include');
  // A transient note when a unified-search pick was blocked by the hierarchy (already
  // covered by a broader area / already added).
  const [geoNote, setGeoNote] = useState<string | null>(null);

  // Manual edit: stamp sourceType manual + clear all extraction provenance/raw.
  const setTargeting = (patch: Partial<TargetingConfig>) =>
    onChange({
      ...t,
      ...patch,
      sourceType: 'manual',
      sourceCampaignId: null,
      sourceCampaignName: null,
      rawPayload: null,
    });

  // Unified-search pick → hierarchy logic (country supersedes all; state absorbs same-
  // state narrower; a contained narrower is blocked). Reach re-estimates automatically
  // (it keys on the serialised targeting).
  const handleGeoSelect = (result: UnifiedGeoResult) => {
    const apply = geoMode === 'exclude' ? addGeoExclusion : applyGeoSelection;
    const { next, outcome, note } = apply(t, result, { defaultCountryCode: biasCountry });
    if (outcome === 'blocked') {
      setGeoNote(note ?? null);
      return;
    }
    setGeoNote(null);
    setTargeting(next);
  };

  const applyExtracted = (extracted: TargetingConfig, campaignId: string, campaignName: string) => {
    onChange({
      ...extracted,
      sourceType: 'extracted',
      sourceCampaignId: campaignId,
      sourceCampaignName: campaignName,
    });
    setExtractOpen(false);
    setMode('extracted');
  };

  // Mode switch — confirm first if the current mode holds data that would be lost.
  function requestMode(next: AudienceMode) {
    if (next === mode) return;
    const losing = mode === 'manual' ? hasManualData(t) : isExtracted;
    if (losing) setPendingMode(next);
    else doSwitch(next);
  }
  function doSwitch(next: AudienceMode) {
    onChange(EMPTY_MANUAL_TARGETING);
    setMode(next);
    setGeoMode('include');
    setPendingMode(null);
  }

  // Map radius drag writes back to the city/place entry (the chip controls are in
  // SelectedGeoChips; these two stay here because the map handler calls them).
  // Bucket-aware: an excluded city/place writes to t.excluded.{cities,places}.
  const updateCity = (key: string, excluded: boolean, patch: Partial<{ radius: number; distanceUnit: 'kilometer' | 'mile' }>) => {
    if (excluded) setTargeting({ excluded: { ...t.excluded, cities: t.excluded.cities.map((c) => (c.key === key ? { ...c, ...patch } : c)) } });
    else setTargeting({ cities: t.cities.map((c) => (c.key === key ? { ...c, ...patch } : c)) });
  };
  const updatePlace = (key: string, excluded: boolean, patch: Partial<{ radius: number; distanceUnit: 'kilometer' | 'mile' }>) => {
    if (excluded) setTargeting({ excluded: { ...t.excluded, places: t.excluded.places.map((p) => (p.key === key ? { ...p, ...patch } : p)) } });
    else setTargeting({ places: t.places.map((p) => (p.key === key ? { ...p, ...patch } : p)) });
  };

  // ---------- map points + boundaries ----------
  // Point types (cities/places/neighborhoods) AND area types (countries/regions) are
  // key-based geo whose centre isn't stored — resolve each via the generic resolver
  // (which now also returns the Google placeId + bounds the area boundaries need). An
  // entry that won't resolve is omitted (stays a chip; never crashes the map). BOTH
  // the included AND the excluded geo need resolved centres (excluded renders red).
  const e = t.excluded;
  const geoItems: GeoItem[] = [
    ...t.countries.map((code) => ({ type: 'country', key: code, name: countryName(code) })),
    ...t.regions.map((r) => ({ type: 'region', key: r.key, name: r.name, path: r.path ?? null })),
    ...t.cities.map((c) => ({ type: 'city', key: c.key, name: c.name, path: c.path ?? null })),
    ...t.places.map((p) => ({ type: 'place', key: p.key, name: p.name, path: p.path ?? null })),
    ...t.neighborhoods.map((n) => ({ type: 'neighborhood', key: n.key, name: n.name, path: n.path ?? null })),
    // Excluded keyed geo — same resolver keys (type+key), so an excluded city and an
    // included city share one resolution; harmless (the map id-prefixes excluded).
    ...e.regions.map((r) => ({ type: 'region', key: r.key, name: r.name, path: r.path ?? null })),
    ...e.cities.map((c) => ({ type: 'city', key: c.key, name: c.name, path: c.path ?? null })),
    ...e.places.map((p) => ({ type: 'place', key: p.key, name: p.name, path: p.path ?? null })),
    ...e.neighborhoods.map((n) => ({ type: 'neighborhood', key: n.key, name: n.name, path: n.path ?? null })),
  ];
  const geoCenters = useResolvedGeoCenters(geoItems);
  // AU postcode polygons load on demand (per postcode, cached) — included + excluded.
  const zipPolygons = useAuPostcodePolygons([...t.zips, ...e.zips]);
  const mapPoints = [
    ...geoMapPoints(t),
    ...resolvedGeoPoints({ cities: t.cities, places: t.places, neighborhoods: t.neighborhoods }, geoCenters),
    ...resolvedGeoPoints({ cities: e.cities, places: e.places, neighborhoods: e.neighborhoods }, geoCenters, { excluded: true }),
  ];
  // AREA outlines: country/region via DDS (placeId), AU postcode via ABS GeoJSON.
  // Included + excluded (excluded draws red; no countries in the excluded bucket).
  const mapBoundaries = [
    ...geoBoundaries(
      {
        countries: t.countries.map((code) => ({ key: code, name: countryName(code) })),
        regions: t.regions,
        zips: t.zips,
      },
      geoCenters,
      zipPolygons,
    ),
    ...geoBoundaries({ countries: [], regions: e.regions, zips: e.zips }, geoCenters, zipPolygons, { excluded: true }),
  ];

  // Map radius drag → write back to the RIGHT field with the RIGHT bounds for the
  // point's kind (cities keep the 17km-min city band; places + pins keep the 1km band).
  // point.excluded routes the write to the included vs excluded bucket.
  const handleMapResize = (point: GeoMapPoint, radius: number) => {
    const clamped = clampRadiusForPoint(point, radius);
    if (point.kind === 'city' && point.geoKey) updateCity(point.geoKey, point.excluded, { radius: clamped });
    else if (point.kind === 'place' && point.geoKey) updatePlace(point.geoKey, point.excluded, { radius: clamped });
    else if (point.kind === 'custom' && point.index !== null) updateCustomLocation(point.excluded, point.index, { radius: clamped });
  };
  // Only custom pins are draggable (city/place/neighborhood centres are fixed by key).
  const handleMapMove = (point: GeoMapPoint, latitude: number, longitude: number) => {
    if (point.kind === 'custom' && point.index !== null) moveCustomLocation(point.excluded, point.index, latitude, longitude);
  };

  // ---------- custom map pins (bucket-aware) ----------
  const customList = (excluded: boolean) => (excluded ? t.excluded.customLocations : t.customLocations);
  const setCustomList = (excluded: boolean, next: TargetingConfig['customLocations']) =>
    excluded ? setTargeting({ excluded: { ...t.excluded, customLocations: next } }) : setTargeting({ customLocations: next });
  // A map click drops an INCLUDED pin — UNLESS excluded pins are enabled AND the search
  // toggle is on Exclude (gated: EXCLUDED_CUSTOM_LOCATIONS_SUPPORTED). While the gate is
  // off, pins are always included (Meta pin-exclusion via Zernio is unverified).
  const canExcludePins = EXCLUDED_CUSTOM_LOCATIONS_SUPPORTED;
  const addCustomLocation = (latitude: number, longitude: number) => {
    const excluded = canExcludePins && geoMode === 'exclude';
    setCustomList(excluded, [
      ...customList(excluded),
      { latitude, longitude, radius: defaultCustomLocationRadius('kilometer'), distanceUnit: 'kilometer', name: null },
    ]);
  };
  const updateCustomLocation = (
    excluded: boolean,
    index: number,
    patch: Partial<TargetingConfig['customLocations'][number]>,
  ) => setCustomList(excluded, customList(excluded).map((c, i) => (i === index ? { ...c, ...patch } : c)));
  const moveCustomLocation = (excluded: boolean, index: number, latitude: number, longitude: number) =>
    updateCustomLocation(excluded, index, { latitude, longitude });
  // Radius from the map drag (already in the pin's unit) or the input — clamp into band.
  const resizeCustomLocation = (excluded: boolean, index: number, radius: number) => {
    const c = customList(excluded)[index];
    if (!c) return;
    updateCustomLocation(excluded, index, { radius: clampCustomLocationRadius(radius, c.distanceUnit) });
  };
  const setCustomLocationUnit = (excluded: boolean, index: number, unit: 'kilometer' | 'mile') => {
    const c = customList(excluded)[index];
    if (!c) return;
    updateCustomLocation(excluded, index, { distanceUnit: unit, radius: clampCustomLocationRadius(c.radius, unit) });
  };
  const removeCustomLocation = (excluded: boolean, index: number) =>
    setCustomList(excluded, customList(excluded).filter((_, i) => i !== index));
  // Move a pin between buckets (flip) — preserves its coords/radius/unit/name.
  const flipCustomLocation = (excluded: boolean, index: number) => {
    const c = customList(excluded)[index];
    if (!c) return;
    const fromNext = customList(excluded).filter((_, i) => i !== index);
    const toNext = [...customList(!excluded), c];
    setTargeting(
      excluded
        ? { excluded: { ...t.excluded, customLocations: fromNext }, customLocations: toNext }
        : { customLocations: fromNext, excluded: { ...t.excluded, customLocations: toNext } },
    );
  };

  // The custom-pin list for ONE bucket (included = slate, excluded = red). Each pin has
  // radius/unit controls, a flip-to-other-bucket button, and remove.
  const renderPinList = (excluded: boolean) => {
    const list = customList(excluded);
    if (list.length === 0) return null;
    const noun = excluded ? 'Excluded pin' : 'Pin';
    return (
      <div className="space-y-1">
        {excluded && <p className="text-[10px] font-semibold uppercase tracking-wide text-red-500">Excluded pins</p>}
        <ul className="space-y-2">
          {list.map((c, i) => (
            <li
              key={i}
              className={`flex flex-wrap items-center gap-2 rounded-md px-2 py-1.5 text-xs ${
                excluded ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-700'
              }`}
            >
              <span className="font-mono">
                {c.latitude.toFixed(4)}, {c.longitude.toFixed(4)}
              </span>
              <span className="ml-auto flex items-center gap-1.5">
                <input
                  type="number"
                  min={CUSTOM_LOCATION_RADIUS_BOUNDS[c.distanceUnit].min}
                  max={CUSTOM_LOCATION_RADIUS_BOUNDS[c.distanceUnit].max}
                  value={c.radius}
                  onChange={(ev) => updateCustomLocation(excluded, i, { radius: Number(ev.target.value) })}
                  onBlur={(ev) => resizeCustomLocation(excluded, i, Number(ev.target.value))}
                  aria-label={`${noun} ${i + 1} radius`}
                  className="w-14 rounded border border-slate-300 px-1 py-0.5 text-xs"
                />
                <select
                  value={c.distanceUnit}
                  onChange={(ev) => setCustomLocationUnit(excluded, i, ev.target.value as 'kilometer' | 'mile')}
                  aria-label={`${noun} ${i + 1} unit`}
                  className="rounded border border-slate-300 px-1 py-0.5 text-xs"
                >
                  <option value="kilometer">km</option>
                  <option value="mile">mi</option>
                </select>
                {/* Flip between buckets. An EXCLUDED pin always gets flip-to-include (to
                    move legacy data back); an INCLUDED pin gets flip-to-exclude ONLY when
                    pin-exclusion is enabled (gated — Meta pin-exclusion is unverified). */}
                {(excluded || canExcludePins) && (
                  <button
                    type="button"
                    aria-label={excluded ? `Include pin ${i + 1} instead` : `Exclude pin ${i + 1} instead`}
                    title={excluded ? 'Move to included' : 'Move to excluded'}
                    onClick={() => flipCustomLocation(excluded, i)}
                    className={excluded ? 'text-red-400 hover:text-green-600' : 'text-slate-400 hover:text-red-600'}
                  >
                    {excluded ? <Plus className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5" />}
                  </button>
                )}
                <button
                  type="button"
                  aria-label={`Remove ${excluded ? 'excluded ' : ''}pin ${i + 1}`}
                  onClick={() => removeCustomLocation(excluded, i)}
                  className="text-slate-400 hover:text-red-600"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Audience</h3>

      <RadioGroup
        ariaLabel="Audience mode"
        value={mode}
        options={[
          { value: 'manual', label: 'Build from scratch' },
          { value: 'extracted', label: 'Extract from existing campaign' },
        ]}
        onChange={requestMode}
      />

      {pendingMode && (
        <Dialog open onOpenChange={(o) => !o && setPendingMode(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Switch targeting mode?</DialogTitle>
              <DialogDescription>Switching will clear your current targeting. Continue?</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPendingMode(null)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={() => doSwitch(pendingMode)}>
                Clear &amp; switch
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {extractOpen && (
        <TargetingExtractionModal
          accountId={accountId}
          currency={currency}
          onApply={(a) => applyExtracted(a.targeting, a.campaignId, a.campaignName)}
          onClose={() => setExtractOpen(false)}
        />
      )}

      {/* ---------- Manual mode ---------- */}
      {mode === 'manual' && (
        <>
          {/* Locations — ONE unified search across all geo types (countries, states,
              cities, suburbs/places, neighborhoods, postcodes) with full breadcrumbs.
              Selecting applies geographic-hierarchy replacement (country supersedes all;
              a state absorbs same-state narrower selections). */}
          <div className="space-y-2">
            <UnifiedGeoSearch
              accountId={accountId}
              biasCountry={biasCountry}
              onSelect={handleGeoSelect}
              mode={geoMode}
              onModeChange={setGeoMode}
            />
            {geoNote && <p className="text-xs text-amber-600">{geoNote}</p>}
            <SelectedGeoChips targeting={t} setTargeting={setTargeting} />
          </div>

          {/* Custom locations (map pins) — ADDITIVE point-radius targeting, independent
              of the mutually-exclusive geo level above. Coexists with cities. */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Targeting map</label>
              <p className="mt-0.5 text-xs text-slate-400">
                Selected cities show as circles (drag the edge to adjust the city radius); excluded areas show in
                red. Click the map to drop a custom pin — a radius around a precise point.{' '}
                {canExcludePins
                  ? 'With the search toggle on Exclude, a dropped pin carves that area out instead.'
                  : 'Custom pins are always included.'}
              </p>
            </div>
            <AudienceMap
              points={mapPoints}
              boundaries={mapBoundaries}
              editable
              onAddPin={addCustomLocation}
              onMove={handleMapMove}
              onResize={handleMapResize}
            />
            {renderPinList(false)}
            {renderPinList(true)}
          </div>

          {/* Location targeting (D2.15 — home vs recent) */}
          <div className="max-w-md">
            <RadioGroup
              label="Location targeting"
              value={t.locationTypes}
              options={[
                { value: 'home', label: 'People who live in this area' },
                { value: 'home_recent', label: 'People who live or have recently been in this area' },
                { value: 'recent', label: 'People recently in this area' },
              ]}
              onChange={(v) => setTargeting({ locationTypes: v })}
            />
            <p className="mt-1 text-xs text-slate-400">
              For franchise marketing, “live in” is recommended to reach genuine local customers. “Live or
              recent” includes travelers.
            </p>
          </div>

          {/* Age */}
          <div className="max-w-md">
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Age range</label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min={13}
                max={65}
                value={t.ageMin}
                onChange={(e) => setTargeting({ ageMin: Number(e.target.value) })}
                aria-label="Minimum age"
                className="w-20 rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
              <span className="text-slate-400">to</span>
              <input
                type="number"
                min={13}
                max={65}
                value={t.ageMax}
                onChange={(e) => setTargeting({ ageMax: Number(e.target.value) })}
                aria-label="Maximum age"
                className="w-20 rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
            <p className="mt-1 text-xs text-slate-400">Reach people aged {t.ageMin}–{t.ageMax}.</p>
          </div>

          {/* Gender */}
          <RadioGroup
            label="Gender"
            value={t.gender}
            options={[
              { value: 'all', label: 'All' },
              { value: 'male', label: 'Male' },
              { value: 'female', label: 'Female' },
            ]}
            onChange={(g) => setTargeting({ gender: g })}
          />

          {/* Interests */}
          <TargetingSearchField
            label="Interests"
            placeholder={accountId ? 'Search interests…' : 'Pick an account first'}
            searchKey="interests"
            search={(q) => (accountId ? searchInterests(accountId, q).then((r) => r.results) : Promise.resolve([]))}
            selected={t.interests.map((i) => ({ id: i.id, name: i.name }))}
            onAdd={(r) => setTargeting({ interests: [...t.interests, { id: r.id, name: r.name }] })}
            onRemove={(id) => setTargeting({ interests: t.interests.filter((i) => i.id !== id) })}
          />
        </>
      )}

      {/* ---------- Extracted mode ---------- */}
      {mode === 'extracted' &&
        (isExtracted ? (
          <div className="space-y-3">
            <ExtractedTargetingSummary
              rawPayload={t.rawPayload}
              campaignName={t.sourceCampaignName}
              adSetName={t.sourceAdSetName}
            />
            {/* Read-only map of the extracted geo (places + custom locations;
                exclusions in red). Editing is locked for extracted targeting. */}
            {mapPoints.length > 0 && (
              <AudienceMap
                points={mapPoints}
                boundaries={mapBoundaries}
                editable={false}
                onAddPin={() => {}}
                onMove={() => {}}
                onResize={() => {}}
              />
            )}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setExtractOpen(true)}>
                Replace
              </Button>
              <Button variant="ghost" size="sm" onClick={() => requestMode('manual')}>
                Switch to manual
              </Button>
            </div>
          </div>
        ) : (
          <div className="rounded-md border border-dashed border-slate-300 p-6 text-center">
            <p className="mb-3 text-sm text-slate-600">
              Copy the audience from a previous Lead Gen or Traffic campaign. The full Meta targeting is preserved
              and applied at publish.
            </p>
            <Button onClick={() => setExtractOpen(true)} disabled={!accountId}>
              Extract from existing campaign
            </Button>
          </div>
        ))}
    </div>
  );
}
